import React, { useMemo, useState, useEffect, useRef, forwardRef } from "react";

import {
  faSort,
  faSortDown,
  faSortUp,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Table as BTable,
  Button,
  Col,
  Form,
  InputGroup,
  Pagination,
  Row,
  Alert,
} from "react-bootstrap";
import { Filter, Info } from "react-feather";
import {
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
  useAsyncDebounce,
  useRowSelect,
} from "react-table";

import Loader from "../Loader";
import GlobalFilter from "./GlobalFilter";
import ColumnFilter from "./ColumnFilter";
import DownloadCSV from "./DownloadCSV";

const ServerSideTable = ({
  columns,
  data,
  fetchData,
  isLoading,
  paginationOptions,
  handleRowClick,
  initialFilterValues,
  downloadFile = false,
  layoutFixed = true,
  totalCount,
  setSelectedRows = () => {},
  useCheckboxes = false,
}) => {
  const [showFilters, setShowFilters] = useState(
    initialFilterValues ? true : false
  );

  const isFirstRender = useRef(true);

  const defaultColumn = useMemo(
    () => ({
      Filter: ColumnFilter,
    }),
    []
  );

  const IndeterminateCheckbox = forwardRef(
    ({ indeterminate, ...rest }, ref) => {
      const defaultRef = useRef();
      const resolvedRef = ref || defaultRef;

      useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate;
      }, [resolvedRef, indeterminate]);

      return (
        <>
          <Form.Check
            type="checkbox"
            ref={resolvedRef}
            onClick={(e) => e.stopPropagation()}
            {...rest}
          />
        </>
      );
    }
  );

  const {
    state: {
      globalFilter,
      filters,
      pageIndex,
      pageSize,
      sortBy,
      selectedRowIds,
    },
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    selectedFlatRows,
    // Global Filter
    preGlobalFilteredRows,
    setGlobalFilter,
    // Filters
    setAllFilters,
    // Pagination
    page,
    pageCount,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    previousPage,
    nextPage,
    setPageSize,
  } = useTable(
    // Table Options
    {
      initialState: {
        filters: initialFilterValues ? initialFilterValues.filters : [],
        pageIndex: paginationOptions.pageIndex,
        pageSize: paginationOptions.pageSize,
      },
      columns,
      data,
      defaultColumn,
      // Global Filter Options
      manualGlobalFilter: true,
      autoResetGlobalFilter: false,
      // Filter Options
      manualFilters: true,
      autoResetFilters: false,
      // Sorting Options
      manualSortBy: true,
      disableMultiSort: false,
      autoResetSortBy: false,
      // Pagination Options
      manualPagination: true,
      autoResetPage: true,
      pageCount: paginationOptions.pageCount,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      useCheckboxes &&
        hooks.visibleColumns.push((columns) => [
          {
            id: "selection",
            Header: ({ getToggleAllPageRowsSelectedProps }) => (
              <div className="text-center">
                <IndeterminateCheckbox
                  {...getToggleAllPageRowsSelectedProps()}
                />
              </div>
            ),
            Cell: ({ row }) => (
              <div className="text-center">
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              </div>
            ),
            disableSortBy: true,
          },
          ...columns,
        ]);
    }
  );

  const fetchDataDebounced = useAsyncDebounce(fetchData, 500);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false; // Skip the initial fetch
      return;
    }

    fetchDataDebounced({
      globalFilter,
      filters,
      pageIndex,
      pageSize,
      sortBy,
    });
  }, [fetchDataDebounced, pageIndex, pageSize, sortBy, globalFilter, filters]);

  // Boolean for filter customization
  const hasFilters = filters.length > 0;

  // Updates the selected rows from checkboxes.
  useEffect(() => {
    setSelectedRows(selectedFlatRows.map((row) => row.original));
  }, [selectedRowIds]);

  return (
    <>
      <div className="d-flex justify-content-between mb-3">
        <div className="d-flex align-items-center me-3">
          <GlobalFilter
            manualGlobalFilter={true}
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            data={data}
            totalCount={totalCount}
          />
          <Button
            className="ms-3"
            variant={hasFilters ? "outline-warning" : "outline-secondary"}
            onClick={() => {
              setShowFilters(!showFilters);
            }}
          >
            <Filter size={16} /> Filters
          </Button>
          {hasFilters && (
            <Button
              variant="outline-danger"
              className="ms-3"
              onClick={() => setAllFilters([])}
            >
              <Filter size={16} /> Clear All
            </Button>
          )}
        </div>
        {downloadFile && preGlobalFilteredRows.length > 0 && (
          <DownloadCSV
            className="ms-3"
            data={preGlobalFilteredRows.map(
              (filteredRow) => filteredRow.original
            )}
            headers={columns}
            filename={downloadFile}
          />
        )}
      </div>
      <BTable
        striped
        hover
        {...getTableProps()}
        style={{ tableLayout: layoutFixed ? "fixed" : "" }}
      >
        <thead>
          {headerGroups.map((headerGroup, index) => (
            <tr key={index} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th key={column.id}>
                  <div
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    {column.render("Header")}
                    <span>
                      {column.canSort &&
                        (column.isSorted ? (
                          column.isSortedDesc ? (
                            <FontAwesomeIcon
                              icon={faSortDown}
                              className="ms-2"
                            />
                          ) : (
                            <FontAwesomeIcon icon={faSortUp} className="ms-2" />
                          )
                        ) : (
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        ))}
                    </span>
                  </div>
                  <div>
                    {column.canFilter && showFilters
                      ? column.render("Filter")
                      : null}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        {isLoading ? (
          <tbody>
            <tr>
              <td colSpan={headerGroups[0].headers.length}>
                <Loader />
              </td>
            </tr>
          </tbody>
        ) : data.length > 0 ? (
          <tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  onClick={handleRowClick ? () => handleRowClick(row) : null}
                  className={handleRowClick ? "cursor-pointer" : ""}
                >
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        ) : (
          <tbody>
            <tr>
              <td colSpan={headerGroups[0].headers.length}>
                <h3 className="text-center">No Results</h3>
              </td>
            </tr>
          </tbody>
        )}
      </BTable>
      <Row className="mt-4">
        <Col md={9}>
          <span className="mx-2">
            Page{" "}
            <strong>
              {pageIndex + 1} of {pageCount}
            </strong>
          </span>
          <span className="ms-3 me-2">Show:</span>
          <Form.Select
            className="d-inline-block w-auto"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
            }}
          >
            {[10, 20, 50, 100, 200].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </Form.Select>

          <span className="ms-3 me-2">Go to page:</span>
          <Form.Control
            className="d-inline-block"
            type="number"
            min={1}
            max={pageCount}
            value={pageIndex + 1}
            onChange={(e) => {
              const page =
                !e.target.value || Number(e.target.value) <= 0
                  ? 1
                  : Number(e.target.value) > pageCount
                  ? pageCount
                  : Number(e.target.value);
              gotoPage(page - 1);
            }}
            style={{ width: "75px" }}
            autoComplete="off"
          />
        </Col>
        <Col md={3}>
          <Pagination className="float-end">
            <Pagination.First
              onClick={() => gotoPage(0)}
              disabled={!canPreviousPage}
            />
            <Pagination.Prev
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
            />
            <Pagination.Item
              onClick={() => gotoPage(0)}
              active={1 === pageIndex + 1}
            >
              {1}
            </Pagination.Item>
            {pageOptions.map((page, index) => {
              if (
                (page + 1 === pageIndex + 1 - 3 && page + 1 !== 1) ||
                (page + 1 === pageIndex + 1 + 3 && page + 1 !== pageCount)
              ) {
                return <Pagination.Ellipsis key={index} disabled />;
              }
              if (
                page + 1 >= pageIndex + 1 - 2 &&
                page + 1 <= pageIndex + 1 + 2 &&
                page + 1 !== 1 &&
                page + 1 !== pageCount
              ) {
                return (
                  <Pagination.Item
                    key={index}
                    onClick={() => gotoPage(page)}
                    active={page === pageIndex}
                  >
                    {page + 1}
                  </Pagination.Item>
                );
              }
              return null;
            })}
            {pageCount !== 1 && (
              <Pagination.Item
                onClick={() => gotoPage(pageCount - 1)}
                active={pageCount === pageIndex + 1}
              >
                {pageCount}
              </Pagination.Item>
            )}
            <Pagination.Next
              onClick={() => nextPage()}
              disabled={!canNextPage}
            />
            <Pagination.Last
              onClick={() => gotoPage(pageCount - 1)}
              disabled={!canNextPage}
            />
          </Pagination>
        </Col>
      </Row>
    </>
  );
};

export default ServerSideTable;
